import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { ISiteMapContentItem, ISiteMapContentItemViewModel } from '@fi-shared/models/site-map';
import { ObjectTypes } from '@fi-shared/types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ObjectTypesService } from '@fi-app/utilities/fi-services/object-types.service';
import { SiteMapHelperService } from '@fi-shared/fi-shared-services/site-map-helper.service';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { AppTextService } from '@fi-shared/app-text';
import { SiteMapDndType, SiteMapDndTypeByObjectType } from '@fi-shared/types/site-map-dnd-types';
import { ISiteMapItemDropEvent, ISiteMapItemPinnedChangeEvent } from '@fi-app/site-map/site-map';
import { fiGetMediaBankFileIcon } from '@fi-shared/utils';

@Component({
    selector: 'fi-site-map-table-item-new',
    templateUrl: './site-map-table-item-new.component.html',
    styleUrls: ['./site-map-table-item-new.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-site-map-table-item-new' },
    standalone: false,
})
export class SiteMapTableItemNewComponent implements OnInit, OnDestroy {
    @Input() dataSet: ISiteMapContentItem[];
    @Input() parentData: ISiteMapContentItem;
    @Input() siteMapViewData: ISiteMapContentItemViewModel[];
    @Input() isViewMode: boolean;
    @Input() isExpanded: boolean = true;
    @Input() tagId: number;
    @Output() elementDrop = new EventEmitter<ISiteMapItemDropEvent>();
    @Input() dropListName: string[] = [];
    @Output() itemPinnedChange = new EventEmitter<ISiteMapItemPinnedChangeEvent>();

    constructor(
        private objectTypesService: ObjectTypesService,
        private cdr: ChangeDetectorRef,
        private siteMapHelperService: SiteMapHelperService,
        private appTextService: AppTextService,
    ) {}

    public ngOnInit(): void {
        this.setSubscriptions();
    }

    protected get siteMapContentItems(): ISiteMapContentItem[] {
        return this.dataSet.filter((contentItem: ISiteMapContentItem) => this.isSiteMapViewDataAvailable(contentItem));
    }

    private isSiteMapViewDataAvailable(contentItem: ISiteMapContentItem): boolean {
        return !!this.siteMapViewData.find((viewData: ISiteMapContentItemViewModel) => {
            return (
                viewData.objectType === contentItem.ObjectType &&
                viewData.objectId === contentItem.ObjectId &&
                viewData.parentId === contentItem.ParentId
            );
        });
    }

    protected onDndStart(objectType: ObjectTypes): void {
        setTimeout(() => {
            this.siteMapHelperService.setCurrentDraggedSitemapType(this.getDndType(objectType));
        }, 0);
    }

    protected resetCurrentDraggedSitemapType(): void {
        this.siteMapHelperService.setCurrentDraggedSitemapType(undefined);
    }

    private setSubscriptions(): void {
        this.siteMapHelperService.onRecalculateSiteMapItemDepth$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.cdr.detectChanges());
    }

    protected toggleItemPin(sitemapContentItem: ISiteMapContentItem): void {
        setTimeout(() => {
            sitemapContentItem.IsPinned = !sitemapContentItem.IsPinned;
            this.itemPinnedChange.emit({
                item: sitemapContentItem,
                targetTagId: this.tagId,
            });
            this.cdr.markForCheck();
        });
    }

    protected getPaddingLeft(contentItem: ISiteMapContentItem): string {
        return this.siteMapHelperService.getSideMapContentItemPaddingLeft(
            this.getCurrentViewData(contentItem)?.level ?? 0,
        );
    }

    protected getElementName(contentItem: ISiteMapContentItem): string {
        return this.getCurrentViewData(contentItem)?.fullName ?? contentItem.Name;
    }

    protected getElementContainerClass(contentItem: ISiteMapContentItem): string {
        const viewData: ISiteMapContentItemViewModel = this.getCurrentViewData(contentItem);
        const classes: string[] = ['level-' + viewData.level];
        if (this.isObjectTypeTag(contentItem.ObjectType)) {
            classes.push('item-tag');
        }
        return classes.join(' ');
    }

    private getCurrentViewData(contentItem: ISiteMapContentItem): ISiteMapContentItemViewModel {
        return this.siteMapViewData?.find((item: ISiteMapContentItemViewModel) => {
            return (
                item.objectId === contentItem.ObjectId &&
                item.objectType === contentItem.ObjectType &&
                item.parentId === contentItem.ParentId
            );
        });
    }

    protected getItemContentTypeLabel(contentItem: ISiteMapContentItem): string {
        if (this.isObjectTypeTag(contentItem.ObjectType)) {
            return;
        }

        return this.objectTypesService.GetNameByObjectType(contentItem.ObjectType);
    }

    protected getBackgroundImage(contentItem: ISiteMapContentItem): string {
        return this.siteMapHelperService.getBackgroundImage(contentItem);
    }

    protected isContentItemNotShared(contentItem: ISiteMapContentItem): boolean {
        return (
            (!contentItem.HasViewAccess || !contentItem.IsPublished) && !this.isObjectTypeTag(contentItem.ObjectType)
        );
    }

    protected getNotSharedLabel(contentItem: ISiteMapContentItem): string {
        const notSharedTextId: string = !contentItem.IsPublished ? 'common.not_shared' : 'common.not_shared_with_you';
        return this.appTextService.get(notSharedTextId);
    }

    protected isExpandAvailable(data: ISiteMapContentItem): boolean {
        return this.isObjectTypeTag(data.ObjectType);
    }

    protected isObjectTypeTag(objectType: ObjectTypes): boolean {
        return this.siteMapHelperService.isObjectTypeTag(objectType);
    }

    protected isContentItemPreviewImageShown(contentItem: ISiteMapContentItem): boolean {
        return this.siteMapHelperService.isContentItemPreviewImageShown(this.isViewMode, contentItem);
    }

    protected isMediaBankPreviewIconShown(contentItem: ISiteMapContentItem): boolean {
        return this.siteMapHelperService.isMediaBankPreviewIconShown(this.isViewMode, contentItem);
    }

    protected isContentItemDefaultIconShown(contentItem: ISiteMapContentItem): boolean {
        return this.siteMapHelperService.isContentItemDefaultIconShown(this.isViewMode, contentItem);
    }

    protected getDefaultItemIconClass(contentItem: ISiteMapContentItem): { [p: string]: boolean } {
        return this.siteMapHelperService.getDefaultItemIconClass(contentItem);
    }

    protected getUiSref(objectType: ObjectTypes): string {
        return this.objectTypesService.getObjectViewStateName(objectType);
    }

    protected onElementDrop($event: ISiteMapItemDropEvent): void {
        this.elementDrop.emit($event);
    }

    protected onItemPinnedChanged($event: ISiteMapItemPinnedChangeEvent): void {
        this.itemPinnedChange.emit($event);
    }

    protected getDndType(objectType: ObjectTypes): SiteMapDndType {
        return SiteMapDndTypeByObjectType[objectType];
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly FiInfoIconMode = FiInfoIconMode;
    protected readonly fiGetMediaBankFileIcon = fiGetMediaBankFileIcon;
}
